import app from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import firebaseConfig from "./config";

class Firebase {
  constructor() {
    app.initializeApp(firebaseConfig);
    this.storage = app.storage();
    this.db = app.firestore();
    this.auth = app.auth();
  }

  // AUTH ACTIONS ------------

  createAccount = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  signIn = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password);

  signInWithGoogle = () =>
    this.auth.signInWithPopup(new app.auth.GoogleAuthProvider());

  signInWithFacebook = () =>
    this.auth.signInWithPopup(new app.auth.FacebookAuthProvider());

  signInWithGithub = () =>
    this.auth.signInWithPopup(new app.auth.GithubAuthProvider());

  signOut = () => this.auth.signOut();

  passwordReset = (email) => this.auth.sendPasswordResetEmail(email);

  addUser = (id, user) =>{this.db.collection("users").doc(id).set(user);}

  getUser = (id) => this.db.collection("users").doc(id).get();

  getAllUsers = () => this.db.collection("users").get();

  getAllReadyProfiles = () => this.db.collection("readyProds").get();

  getProfileViews = () => this.db.collection("profileViews").get();

  trackProfileView = (view) => this.db.collection("profileViews").add({
    ...view,
    viewedAt: app.firestore.FieldValue.serverTimestamp(),
  });

  updateUserByAdmin = (id, updates) =>
    this.db.collection("users").doc(id).update(updates);

  activateUserProfile = async (profileId, userId) => {
    const normalizedId = profileId.trim();

    if (!normalizedId || normalizedId.includes("/")) {
      throw new Error("Profile link ID is required and cannot contain a slash.");
    }

    const profileRef = this.db.collection("readyProds").doc(normalizedId);
    const snapshot = await profileRef.get();

    if (snapshot.exists && snapshot.data().user !== userId) {
      throw new Error("This profile link ID is already assigned to another user.");
    }

    return profileRef.set({ user: userId }, { merge: true });
  };

  getAccountingData = async () => {
    const collectionNames = [
      "accountingInventory",
      "accountingPurchases",
      "accountingOrders",
      "accountingLosses",
      "accountingExpenses",
      "accountingCollections",
      "accountingOffers",
      "accountingSettings",
    ];
    const snapshots = await Promise.all(
      collectionNames.map((collectionName) => this.db.collection(collectionName).get())
    );

    return collectionNames.reduce((data, collectionName, index) => ({
      ...data,
      [collectionName]: snapshots[index].docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })),
    }), {});
  };

  addAccountingStock = async ({ inventoryId, ...purchase }) => {
    const inventoryRef = inventoryId
      ? this.db.collection("accountingInventory").doc(inventoryId)
      : this.db.collection("accountingInventory").doc();
    const purchaseRef = this.db.collection("accountingPurchases").doc();

    return this.db.runTransaction(async (transaction) => {
      const snapshot = await transaction.get(inventoryRef);
      const current = snapshot.exists ? snapshot.data() : {};
      const oldQuantity = Number(current.quantity) || 0;
      const oldAverageCost = Number(current.averageUnitCost) || 0;
      const addedQuantity = Number(purchase.quantity) || 0;
      const unitPrice = Number(purchase.unitPrice) || 0;
      const quantity = oldQuantity + addedQuantity;
      const averageUnitCost = snapshot.exists && !purchase.updateUnitPrice
        ? oldAverageCost
        : unitPrice;
      const timestamp = app.firestore.FieldValue.serverTimestamp();
      const inventory = {
        name: purchase.name,
        size: purchase.size,
        color: purchase.color,
        quantity,
        averageUnitCost,
        inventoryValue: quantity * averageUnitCost,
        updatedAt: timestamp,
      };

      if (!snapshot.exists) inventory.createdAt = timestamp;
      transaction.set(inventoryRef, inventory, { merge: true });
      transaction.set(purchaseRef, {
        inventoryId: inventoryRef.id,
        name: purchase.name,
        size: purchase.size,
        color: purchase.color,
        quantity: addedQuantity,
        unitPrice,
        total: addedQuantity * unitPrice,
        date: purchase.date,
        note: purchase.note || "",
        createdAt: timestamp,
      });

      return inventoryRef.id;
    });
  };

  createAccountingOrder = async (order) => {
    const orderRef = this.db.collection("accountingOrders").doc();
    const materialIds = Array.from(new Set(
      order.items.filter((item) => item.materialId).map((item) => item.materialId)
    ));
    const materialRefs = materialIds.map(
      (materialId) => this.db.collection("accountingInventory").doc(materialId)
    );

    return this.db.runTransaction(async (transaction) => {
      const materialSnapshots = await Promise.all(
        materialRefs.map((materialRef) => transaction.get(materialRef))
      );
      const materials = materialSnapshots.reduce((items, snapshot) => ({
        ...items,
        [snapshot.id]: snapshot.data(),
      }), {});
      const requirements = {};

      order.items.forEach((item) => {
        if (!item.materialId) return;
        requirements[item.materialId] = (requirements[item.materialId] || 0)
          + (Number(item.quantity) || 0) * (Number(item.materialQuantityPerUnit) || 1);
      });

      materialRefs.forEach((materialRef) => {
        const material = materials[materialRef.id];
        if (!material) throw new Error("A selected raw material no longer exists.");
        if ((Number(material.quantity) || 0) < requirements[materialRef.id]) {
          throw new Error(`Not enough stock for ${material.name} ${material.size} ${material.color}.`);
        }
      });

      materialRefs.forEach((materialRef) => {
        const material = materials[materialRef.id];
        const quantity = (Number(material.quantity) || 0) - requirements[materialRef.id];
        transaction.update(materialRef, {
          quantity,
          inventoryValue: quantity * (Number(material.averageUnitCost) || 0),
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
        });
      });

      const items = order.items.map((item) => {
        const material = item.materialId ? materials[item.materialId] : null;
        return {
          ...item,
          materialName: material
            ? `${material.name} - ${material.size} - ${material.color}`
            : "Custom material",
          materialUnitCost: material ? Number(material.averageUnitCost) || 0 : 0,
        };
      });

      transaction.set(orderRef, {
        ...order,
        items,
        createdAt: app.firestore.FieldValue.serverTimestamp(),
      });

      return orderRef.id;
    });
  };

  updateAccountingOrderStatus = (orderId, status) => this.db
    .collection("accountingOrders")
    .doc(orderId)
    .update({ status, updatedAt: app.firestore.FieldValue.serverTimestamp() });

  updateAccountingOrdersStatus = (orderIds, status) => {
    const batch = this.db.batch();
    orderIds.forEach((orderId) => {
      const orderRef = this.db.collection("accountingOrders").doc(orderId);
      batch.update(orderRef, {
        status,
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
      });
    });
    return batch.commit();
  };

  updateAccountingOrder = async (orderId, order) => {
    const orderRef = this.db.collection("accountingOrders").doc(orderId);
    const linkedCollections = await this.db.collection("accountingCollections")
      .where("orderId", "==", orderId)
      .get();
    const courierCollected = linkedCollections.docs.reduce(
      (total, snapshot) => total + (Number(snapshot.data().amount) || 0),
      0
    );
    const productSubtotal = order.items.reduce((total, item) => (
      total + Math.max(
        (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0)
          - (Number(item.discount) || 0),
        0
      )
    ), 0);
    const discountPercentage = Math.min(
      Math.max(Number(order.discountPercentage) || 0, 0),
      100
    );
    const invoiceTotal = productSubtotal * (1 - discountPercentage / 100)
      + (Number(order.shippingCharged) || 0);
    if (invoiceTotal + 0.009 < (Number(order.deposit) || 0) + courierCollected) {
      throw new Error("The order total cannot be less than its collected amount.");
    }

    return this.db.runTransaction(async (transaction) => {
      const orderSnapshot = await transaction.get(orderRef);
      if (!orderSnapshot.exists) throw new Error("The selected order no longer exists.");
      const previousOrder = orderSnapshot.data();
      const previousItems = previousOrder.items || [];
      const materialIds = Array.from(new Set(
        [...previousItems, ...order.items]
          .filter((item) => item.materialId)
          .map((item) => item.materialId)
      ));
      const materialRefs = materialIds.map(
        (materialId) => this.db.collection("accountingInventory").doc(materialId)
      );
      const materialSnapshots = await Promise.all(
        materialRefs.map((materialRef) => transaction.get(materialRef))
      );
      const materials = materialSnapshots.reduce((items, snapshot) => ({
        ...items,
        [snapshot.id]: snapshot.exists ? snapshot.data() : null,
      }), {});
      const getRequirements = (items) => items.reduce((requirements, item) => {
        if (!item.materialId) return requirements;
        return {
          ...requirements,
          [item.materialId]: (requirements[item.materialId] || 0)
            + (Number(item.quantity) || 0) * (Number(item.materialQuantityPerUnit) || 1),
        };
      }, {});
      const previousRequirements = getRequirements(previousItems);
      const nextRequirements = getRequirements(order.items);

      materialRefs.forEach((materialRef) => {
        const material = materials[materialRef.id];
        if (!material) throw new Error("A selected raw material no longer exists.");
        const quantity = (Number(material.quantity) || 0)
          + (previousRequirements[materialRef.id] || 0)
          - (nextRequirements[materialRef.id] || 0);
        if (quantity < 0) throw new Error(`Not enough stock for ${material.name}.`);
        transaction.update(materialRef, {
          quantity,
          inventoryValue: quantity * (Number(material.averageUnitCost) || 0),
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
        });
      });

      const items = order.items.map((item) => {
        const material = item.materialId ? materials[item.materialId] : null;
        return {
          ...item,
          materialName: material
            ? `${material.name} - ${material.size} - ${material.color}`
            : "Custom material",
          materialUnitCost: material ? Number(material.averageUnitCost) || 0 : 0,
        };
      });

      transaction.set(orderRef, {
        ...order,
        items,
        createdAt: previousOrder.createdAt || app.firestore.FieldValue.serverTimestamp(),
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
      });
    });
  };

  deleteAccountingOrder = async (orderId) => {
    const linkedCollections = await this.db.collection("accountingCollections")
      .where("orderId", "==", orderId)
      .limit(1)
      .get();
    if (!linkedCollections.empty) {
      throw new Error("This order has a courier collection and cannot be deleted.");
    }
    const orderRef = this.db.collection("accountingOrders").doc(orderId);

    return this.db.runTransaction(async (transaction) => {
      const orderSnapshot = await transaction.get(orderRef);
      if (!orderSnapshot.exists) throw new Error("The selected order no longer exists.");
      const requirements = (orderSnapshot.data().items || []).reduce((items, item) => {
        if (!item.materialId) return items;
        return {
          ...items,
          [item.materialId]: (items[item.materialId] || 0)
            + (Number(item.quantity) || 0) * (Number(item.materialQuantityPerUnit) || 1),
        };
      }, {});
      const materialRefs = Object.keys(requirements).map(
        (materialId) => this.db.collection("accountingInventory").doc(materialId)
      );
      const materialSnapshots = await Promise.all(
        materialRefs.map((materialRef) => transaction.get(materialRef))
      );

      materialSnapshots.forEach((snapshot) => {
        if (!snapshot.exists) {
          throw new Error("A deducted raw material no longer exists.");
        }
        const material = snapshot.data();
        const quantity = (Number(material.quantity) || 0) + requirements[snapshot.id];
        transaction.update(snapshot.ref, {
          quantity,
          inventoryValue: quantity * (Number(material.averageUnitCost) || 0),
          updatedAt: app.firestore.FieldValue.serverTimestamp(),
        });
      });
      transaction.delete(orderRef);
    });
  };

  addAccountingLoss = async (loss) => {
    const lossRef = this.db.collection("accountingLosses").doc();

    if (loss.type !== "RAW_MATERIAL" || !loss.materialId) {
      return lossRef.set({
        ...loss,
        calculatedAmount: Number(loss.amount) || 0,
        createdAt: app.firestore.FieldValue.serverTimestamp(),
      });
    }

    const materialRef = this.db.collection("accountingInventory").doc(loss.materialId);
    return this.db.runTransaction(async (transaction) => {
      const snapshot = await transaction.get(materialRef);
      if (!snapshot.exists) throw new Error("The selected raw material no longer exists.");
      const material = snapshot.data();
      const lostQuantity = Number(loss.quantity) || 0;
      const currentQuantity = Number(material.quantity) || 0;
      if (lostQuantity > currentQuantity) throw new Error("Loss quantity exceeds available stock.");
      const quantity = currentQuantity - lostQuantity;
      const unitCost = Number(material.averageUnitCost) || 0;

      transaction.update(materialRef, {
        quantity,
        inventoryValue: quantity * unitCost,
        updatedAt: app.firestore.FieldValue.serverTimestamp(),
      });
      transaction.set(lossRef, {
        ...loss,
        materialName: `${material.name} - ${material.size} - ${material.color}`,
        unitCost,
        calculatedAmount: lostQuantity * unitCost,
        createdAt: app.firestore.FieldValue.serverTimestamp(),
      });

      return lossRef.id;
    });
  };

  addAccountingExpense = (expense) => this.db.collection("accountingExpenses").add({
    ...expense,
    amount: Number(expense.amount) || 0,
    createdAt: app.firestore.FieldValue.serverTimestamp(),
  });

  addAccountingCollection = (collection) => this.db.collection("accountingCollections").add({
    ...collection,
    amount: Number(collection.amount) || 0,
    createdAt: app.firestore.FieldValue.serverTimestamp(),
  });

  addAccountingCollectionBatch = (collection, selectedOrders) => {
    const batch = this.db.batch();
    const batchId = this.db.collection("accountingCollections").doc().id;
    const timestamp = app.firestore.FieldValue.serverTimestamp();

    selectedOrders.forEach((order) => {
      const collectionRef = this.db.collection("accountingCollections").doc();
      batch.set(collectionRef, {
        batchId,
        batchTotal: Number(collection.amount) || 0,
        batchOrderCount: selectedOrders.length,
        orderId: order.id,
        customerName: order.customerName,
        amount: Number(order.amount) || 0,
        date: collection.date,
        note: collection.note || "",
        createdAt: timestamp,
      });
    });

    return batch.commit();
  };

  addAccountingOffer = (offer) => this.db.collection("accountingOffers").add({
    ...offer,
    quantity: Number(offer.quantity) || 0,
    regularUnitPrice: Number(offer.regularUnitPrice) || 0,
    offerPrice: Number(offer.offerPrice) || 0,
    active: true,
    createdAt: app.firestore.FieldValue.serverTimestamp(),
  });

  updateAccountingOfferStatus = (offerId, active) => this.db
    .collection("accountingOffers")
    .doc(offerId)
    .update({ active, updatedAt: app.firestore.FieldValue.serverTimestamp() });

  updateAccountingStandPrices = (prices) => this.db
    .collection("accountingSettings")
    .doc("standPrices")
    .set({
      ...prices,
      updatedAt: app.firestore.FieldValue.serverTimestamp(),
    }, { merge: true });

  updateAccountingProductCatalog = (items) => this.db
    .collection("accountingSettings")
    .doc("productCatalog")
    .set({
      items,
      updatedAt: app.firestore.FieldValue.serverTimestamp(),
    });

  passwordUpdate = (password) => this.auth.currentUser.updatePassword(password);

  changePassword = (currentPassword, newPassword) =>
    new Promise((resolve, reject) => {
      this.reauthenticate(currentPassword)
        .then(() => {
          const user = this.auth.currentUser;
          user
            .updatePassword(newPassword)
            .then(() => {
              resolve("Password updated successfully!");
            })
            .catch((error) => reject(error));
        })
        .catch((error) => reject(error));
    });

  reauthenticate = (currentPassword) => {
    const user = this.auth.currentUser;
    const cred = app.auth.EmailAuthProvider.credential(
      user.email,
      currentPassword
    );

    return user.reauthenticateWithCredential(cred);
  };

  updateEmail = (currentPassword, newEmail) =>
    new Promise((resolve, reject) => {
      this.reauthenticate(currentPassword)
        .then(() => {
          const user = this.auth.currentUser;
          user
            .updateEmail(newEmail)
            .then(() => {
              resolve("Email Successfully updated");
            })
            .catch((error) => reject(error));
        })
        .catch((error) => reject(error));
    });

  updateProfile = (id, updates) => {
    this.db.collection("users").doc(id).update(updates);


  }
  onAuthStateChanged = () =>
    new Promise((resolve, reject) => {
      this.auth.onAuthStateChanged((user) => {
        if (user) {
          resolve(user);
        } else {
          reject(new Error("Auth State Changed failed"));
        }
      });
    });

  saveBasketItems = (items, userId) =>
    this.db.collection("users").doc(userId).update({ basket: items });

    clearBasketItems = (userId) =>
    this.db.collection("users").doc(userId).update({ basket: [] });



  setAuthPersistence = () =>
    this.auth.setPersistence(app.auth.Auth.Persistence.LOCAL);

  // // PRODUCT ACTIONS --------------

  getSingleProduct = (id) => this.db.collection("products").doc(id).get();
  getSingleUser = (id) => this.db.collection("users").doc(id).get();
  getuidByPid = (id) => this.db.collection("readyProds").doc(id).get();




  getProducts = (lastRefKey) => {
    let didTimeout = false;

    return new Promise((resolve, reject) => {
      (async () => {
        if (lastRefKey) {
          try {
            const query = this.db
              .collection("products")
              .orderBy(app.firestore.FieldPath.documentId())
              .startAfter(lastRefKey)
              .limit(12);

            const snapshot = await query.get();
            const products = [];
            snapshot.forEach((doc) =>
              products.push({ id: doc.id, ...doc.data() })
            );
            const lastKey = snapshot.docs[snapshot.docs.length - 1];

            resolve({ products, lastKey });
          } catch (e) {
            reject(e?.message || ":( Failed to fetch products.");
          }
        } else {
          const timeout = setTimeout(() => {
            didTimeout = true;
            reject(new Error("Request timeout, please try again"));
          }, 15000);

          try {
            const totalQuery = await this.db.collection("products").get();
            const total = totalQuery.docs.length;
            const query = this.db
              .collection("products")
              .orderBy(app.firestore.FieldPath.documentId())
              .limit(12);
            const snapshot = await query.get();

            clearTimeout(timeout);
            if (!didTimeout) {
              const products = [];
              snapshot.forEach((doc) =>
                products.push({ id: doc.id, ...doc.data() })
              );
              const lastKey = snapshot.docs[snapshot.docs.length - 1];

              resolve({ products, lastKey, total });
            }
          } catch (e) {
            if (didTimeout) return;
            reject(e?.message || ":( Failed to fetch products.");
          }
        }
      })();
    });
  };

  searchProducts = (searchKey) => {
    let didTimeout = false;

    return new Promise((resolve, reject) => {
      (async () => {
        const productsRef = this.db.collection("products");

        const timeout = setTimeout(() => {
          didTimeout = true;
          reject(new Error("Request timeout, please try again"));
        }, 15000);

        try {
          const searchedNameRef = productsRef
            .orderBy("name_lower")
            .where("name_lower", ">=", searchKey)
            .where("name_lower", "<=", `${searchKey}\uf8ff`)
            .limit(12);
          const searchedKeywordsRef = productsRef
            .orderBy("dateAdded", "desc")
            .where("keywords", "array-contains-any", searchKey.split(" "))
            .limit(12);

          // const totalResult = await totalQueryRef.get();
          const nameSnaps = await searchedNameRef.get();
          const keywordsSnaps = await searchedKeywordsRef.get();
          // const total = totalResult.docs.length;

          clearTimeout(timeout);
          if (!didTimeout) {
            const searchedNameProducts = [];
            const searchedKeywordsProducts = [];
            let lastKey = null;

            if (!nameSnaps.empty) {
              nameSnaps.forEach((doc) => {
                searchedNameProducts.push({ id: doc.id, ...doc.data() });
              });
              lastKey = nameSnaps.docs[nameSnaps.docs.length - 1];
            }

            if (!keywordsSnaps.empty) {
              keywordsSnaps.forEach((doc) => {
                searchedKeywordsProducts.push({ id: doc.id, ...doc.data() });
              });
            }

            // MERGE PRODUCTS
            const mergedProducts = [
              ...searchedNameProducts,
              ...searchedKeywordsProducts,
            ];
            const hash = {};

            mergedProducts.forEach((product) => {
              hash[product.id] = product;
            });

            resolve({ products: Object.values(hash), lastKey });
          }
        } catch (e) {
          if (didTimeout) return;
          reject(e);
        }
      })();
    });
  };

  getFeaturedProducts = (itemsCount = 12) =>
    this.db
      .collection("products")
      .where("isFeatured", "==", true)
      .limit(itemsCount)
      .get();

  getRecommendedProducts = (itemsCount = 12) =>
    this.db
      .collection("products")
      .where("isRecommended", "==", true)
      .limit(itemsCount)
      .get();

  addProduct = (id, product) =>
    this.db.collection("products").doc(id).set(product);

  generateKey = () => this.db.collection("products").doc().id;



confirmUserProducts = (order) => this.db.collection("orders").doc(this.db.collection("orders").doc().id).set(order);




  storeImage = async (id, folder, imageFile) => {
    const snapshot = await this.storage.ref(folder).child(id).put(imageFile);
    const downloadURL = await snapshot.ref.getDownloadURL();

    return downloadURL;
  };

  deleteImage = (id) => this.storage.ref("products").child(id).delete();

  editProduct = (id, updates) =>
    this.db.collection("products").doc(id).update(updates);

  removeProduct = (id) => this.db.collection("products").doc(id).delete();
}

const firebaseInstance = new Firebase();

export default firebaseInstance;
